import React, { useEffect, useState, useMemo, useRef } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { TextField, Autocomplete, Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Add from "@mui/icons-material/Add";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import MailOutline from "@mui/icons-material/MailOutline";
import Send from "@mui/icons-material/Send";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmNotice, CrmFilterBar, CrmCard, CrmSectionHeader, CrmRowActions, ListLink, ListTruncate, ListStatus } from "../shared/CrmUi";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../../examples/Footer";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import { appConfirm } from "components/AppDialog";
function SalesLeadTable() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const [destinationCountry, setDestinationCountry] = useState(null);
    const [salesRepOptions, setSalesRepOptions] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentTableData, setAgentTableData] = useState([]);
    const [draftRows, setDraftRows] = useState([]);        // leads export satırları
    const [draftAgentRows, setDraftAgentRows] = useState([]); // agent export satırları (opsiyonel)
    const bodyRef = useRef(null);
    const [pendingDrafts, setPendingDrafts] = useState([]);
    const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
    const [emailPreviewData, setEmailPreviewData] = useState({ to: "", cc: "", subject: "", body: "" });

    const emptyDraft = useMemo(
        () => ({
            to: [],
            subject: "",
            htmlBody: "",
            bodyText: "",
        }),
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const response = await CrudService.getUsers();
                const users = (response.data || []).map((u) => ({
                    id: u.id,
                    name: u.attributes?.name || "Unknown",
                    email: u.attributes?.email || "",
                }));
                setSalesRepOptions(users);
            } catch (err) {
                console.error("Error fetching sales reps:", err);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response = await CrudService.getLeads();
                const normalized = (response.data || []).map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalized);
            } catch (err) {
                console.error("Error fetching leads:", err);
                setNotification({ value: true, text: "Failed to load Leads. Please try again later." });
                setTimeout(() => setNotification({ value: false, text: "" }), 5000);
            }
        })();
    }, []);

    const LEAD_FIELD_LABELS = {
        customer: "Company",
        taxId: "Tax ID",
        customerCountry: "Customer Country",
        city: "City",
        webSite: "Website",
        industry: "Industry",
        companySize: "Company Size",
        logisticsDecision: "Logistics Decision",
        sourceType: "Source Type",
        tradeType: "Trade Type",
        incoterm: "Incoterm",
        commodityOrVertical: "Commodity / Vertical",
        originCountry: "Origin Country",
        originPlaceType: "Origin Place Type",
        originPlaceLabel: "Origin Place",
        destinationCountry: "Destination Country",
        destinationPlaceType: "Destination Place Type",
        destinationPlaceLabel: "Destination Place",
        productServiceModes: "Product / Service Modes",
        serviceMode: "Service Mode",
        projectSubtype: "Project Subtype",
        volumeEstimate: "Volume Estimate",
        leadStatus: "Lead Status",
    };

    const prettifyKey = (key) => {
        if (!key) return "";
        const s = String(key);

        // camelCase -> "Camel Case"
        const withSpaces = s
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/_/g, " ")
            .trim();

        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const getLeadLabel = (key) => LEAD_FIELD_LABELS[key] || prettifyKey(key);

    const COLLAB_PRIORITY = [
        "Exclusive Partner",
        "JV Partner",
        "Strategic Partner",
        "Ad-hoc Partner",
        "Standard Partner",
    ];

    const COLLAB_RANK_MAP = COLLAB_PRIORITY.reduce((acc, label, idx) => {
        acc[label] = idx;
        return acc;
    }, {});

    const collabRank = (agent) => {
        const list = Array.isArray(agent?.byTypeOfCollaboration) ? agent.byTypeOfCollaboration : [];
        if (!list.length) return 999;

        let best = 999;
        for (const t of list) {
            const r = COLLAB_RANK_MAP[t];
            if (typeof r === "number" && r < best) best = r;
        }
        return best;
    };

    const collabLabel = (agent) => {
        const r = collabRank(agent);
        return r === 999 ? "-" : COLLAB_PRIORITY[r];
    };
    const destinationOptions = useMemo(() => {
        const set = new Set();

        (data || []).forEach((lead) => {
            const c = lead?.laneDestination?.country?.trim();
            if (c) set.add(c);
        });

        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [data]);

    const filteredData = useMemo(() => {
        if (!destinationCountry) return data;

        return (data || []).filter((lead) => {
            const c = lead?.laneDestination?.country?.trim() || "";
            return c.toLowerCase() === destinationCountry.toLowerCase();
        });
    }, [data, destinationCountry]);


    const getAgentRows = (list) =>
        list.map((a) => {
            const services = Array.isArray(a?.servicesCountry) ? a.servicesCountry : [];
            const label = collabLabel(a);
            const order = collabRank(a); // Exclusive=0 ... Standard=4, none=999

            const sortScore =
                order === 999 ? 0 : (COLLAB_PRIORITY.length - order); // Exclusive=5 ... Standard=1

            return {
                id: a.id,
                collabOrder: collabRank(a),
                select: (
                    <Checkbox
                        checked={selectedAgentIds.includes(a.id)}
                        onChange={() => toggleAgent(a.id)}
                    />
                ),
                name: a.name || "-",
                globalAgentCountry: a.globalAgentCountry || "-",
                servicesCountry: services.length ? services.join(", ") : "-",
                collaborationSort: `${String(sortScore).padStart(2, "0")} ${label}`, // "05 Exclusive Partner"
                collaboration: label,
            };
        });

    useEffect(() => {
        const run = async () => {
            if (!destinationCountry) {
                setAgents([]);
                setSelectedAgentIds([]);
                return;
            }

            try {
                setAgentsLoading(true);

                const res = await CrudService.getGlobalAgents();
                const normalized = (res.data || []).map((item) => {
                    const attrs = item.attributes || {};

                    const raw = Array.isArray(attrs.byTypeOfCollaboration) ? attrs.byTypeOfCollaboration : [];
                    const byTypeOfCollaboration = raw
                        .map((x) => (typeof x === "string" ? x : x?.label)) // obje ise label al
                        .filter(Boolean);

                    return {
                        id: item.id || item._id,
                        ...attrs,
                        servicesCountry: Array.isArray(attrs.servicesCountry) ? attrs.servicesCountry : [],
                        byTypeOfCollaboration,
                    };
                });

                const dest = destinationCountry.toLowerCase();

                const filtered = normalized
                    .filter((a) => {
                        const countryMatch = String(a?.globalAgentCountry || "").toLowerCase() === dest;
                        const services = Array.isArray(a?.servicesCountry) ? a.servicesCountry : [];
                        const servicesMatch = services.some((x) => String(x || "").toLowerCase() === dest);
                        return countryMatch || servicesMatch;
                    })
                    .sort((a, b) => {
                        // 1) Collaboration priority (Exclusive -> ... -> Standard)
                        const ra = collabRank(a);
                        const rb = collabRank(b);
                        if (ra !== rb) return ra - rb;

                        // 2) Aynı collaboration ise isimle sırala
                        return String(a?.name || "").localeCompare(String(b?.name || ""));
                    });

                setAgents(filtered);
                setSelectedAgentIds([]);
            } catch (e) {
                console.error("Error fetching global agents:", e);
                setAgents([]);
            } finally {
                setAgentsLoading(false);
            }
        };

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationCountry]);

    const toggleAgent = (agentId) => {
        setSelectedAgentIds((prev) =>
            prev.includes(agentId) ? prev.filter((x) => x !== agentId) : [...prev, agentId]
        );
    };

    useEffect(() => {
        const rows = getAgentRows(agents)
            .sort((r1, r2) => {
                if (r1.collabOrder !== r2.collabOrder) return r1.collabOrder - r2.collabOrder;
                return String(r1.name || "").localeCompare(String(r2.name || ""));
            });

        setAgentTableData(rows);
    }, [agents, selectedAgentIds, destinationCountry]);

    const agentDataTableData = useMemo(
        () => ({
            columns: [
                {
                    Header: "Select",
                    accessor: "select",
                    disableSortBy: true,
                    width: "20px",
                    align: "center",
                },
                { Header: "Agent", accessor: "name", disableSortBy: true },
                { Header: "Country", accessor: "globalAgentCountry", disableSortBy: true },
                { Header: "HQ", accessor: "servicesCountry", disableSortBy: true },
                { Header: "Collaboration", accessor: "collaborationSort", disableSortBy: true, Cell: ({ value }) => String(value || "").replace(/^\d+\s+/, ""),},
            ],
            rows: agentTableData,

            // ✅ sadece bu tabloda default sort kapalı
            initialState: { sortBy: [] },
        }),
        [agentTableData]
    );

    const clickEditHandler = (id) => {navigate(`/sales-lead/create-sales-lead/details?id=${id}`);};

    const clickDeleteHandler = async (_, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this Lead?")) return;

            await CrudService.deleteLead(id);
            setData((prev) => prev.filter((x) => x.id !== id));

            setNotification({ value: true, text: "Lead deleted successfully." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        } catch (err) {
            console.error("Error deleting lead:", err);
            setNotification({ value: true, text: "Failed to delete Lead. Please try again later." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };

    const getRows = (list) =>
        list.map((lead) => ({
            id: lead.id,
            customer: (
                <ListLink onClick={() => clickEditHandler(lead.id)}>
                    <ListTruncate text={lead.customer || "—"} max={40} />
                </ListLink>
            ),
            country: lead.customerCountry || "-",
            city: lead.city || "-",
            tradeType: lead.tradeType || "-",
            incoterm: lead.incoterm || "-",
            mode: lead.serviceMode || "-",
            status: lead.leadStatus ? <ListStatus label={lead.leadStatus} tone={/won|converted|qualified/i.test(lead.leadStatus) ? "green" : /lost|rejected|closed/i.test(lead.leadStatus) ? "red" : "blue"} /> : "-",
            sourceType: lead.sourceType || "",
            actions: (
                <CrmRowActions onEdit={() => clickEditHandler(lead.id)} editTitle="Edit Lead" onDelete={(e) => clickDeleteHandler(e, lead.id)} deleteTitle="Delete Lead" />
            ),
        }));

    useEffect(() => {setTableData(getRows(filteredData));}, [filteredData, salesRepOptions]);

    const fetchAllLeads = async () => {
        const all = [];
        let page = 1;
        const pageSize = 200;

        while (true) {
            // CrudService.getLeads pagination desteklemiyorsa bu kısmı sadeleştir
            const res = await CrudService.getLeads({ page, pageSize });

            const rows = (res.data || []).map((item) => ({
                id: item.id,
                ...item.attributes,
            }));

            all.push(...rows);

            const meta = res.meta || res?.data?.meta;
            const pageCount = meta?.pagination?.pageCount;

            const hasNext = pageCount ? page < pageCount : rows.length === pageSize;
            if (!hasNext) break;

            page += 1;
        }

        return all;
    };

    const buildLeadExportRows = (leads) =>
        (leads || []).map((lead) => {
            const origin = lead?.laneOrigin || {};
            const dest = lead?.laneDestination || {};
            const psm = Array.isArray(lead?.productServiceModes) ? lead.productServiceModes : [];
            return {
                customer: lead?.customer || "",
                taxId: lead?.taxId || "",
                customerCountry: lead?.customerCountry || "",
                city: lead?.city || "",
                webSite: lead?.webSite || "",
                industry: Array.isArray(lead?.industry) ? lead.industry.join(", ") : "",
                companySize: lead?.companySize || "",
                logisticsDecision: lead?.logisticsDecision || "",
                sourceType: lead?.sourceType || "",
                tradeType: lead?.tradeType || "",
                incoterm: lead?.incoterm || "",
                commodityOrVertical: lead?.commodityOrVertical || "",
                originCountry: origin?.country || "",
                originPlaceType: origin?.placeType || "",
                originPlaceLabel: origin?.placeLabel || "",
                destinationCountry: dest?.country || "",
                destinationPlaceType: dest?.placeType || "",
                destinationPlaceLabel: dest?.placeLabel || "",
                productServiceModes: psm.length
                    ? psm
                        .map((x) => `${x?.serviceMode || ""}${x?.projectSubtype ? ` / ${x.projectSubtype}` : ""}${x?.volumeEstimate ? ` / ${x.volumeEstimate}` : ""}`)
                        .join(" | ")
                    : "",
                serviceMode: lead?.serviceMode || "",
                projectSubtype: lead?.projectSubtype || "",
                volumeEstimate: lead?.volumeEstimate || "",
                leadStatus: lead?.leadStatus || "",
            };
        });

    const buildAgentExportRows = (agentList) =>
        (agentList || []).map((a) => ({
            agentName: a?.name || "",
            globalAgentCountry: a?.globalAgentCountry || "",
            servicesCountry: Array.isArray(a?.servicesCountry) ? a.servicesCountry.join(", ") : "",
            collaboration: Array.isArray(a?.byTypeOfCollaboration) ? a.byTypeOfCollaboration.join(", ") : "",
        }));

    const exportWorkbook = (sheets, fileName) => {
        const wb = XLSX.utils.book_new();

        sheets.forEach(({ name, rows }) => {
            const ws = XLSX.utils.json_to_sheet(rows || []);
            XLSX.utils.book_append_sheet(wb, ws, name);
        });
        XLSX.writeFile(wb, fileName);
    };

    const handleExportAllLeadsExcel = async () => {
        try {
            const allData = await fetchAllLeads();
            const leadRows = buildLeadExportRows(allData);

            // ✅ Draft Table’a bas (excelde indirilen tüm veriler)
            setDraftRows(leadRows);

            exportWorkbook([{ name: "Leads", rows: leadRows }], `leads_all_${new Date().toISOString().slice(0,10)}.xlsx`);
        } catch (err) {
            console.error("Export failed:", err);
            setNotification({ value: true, text: "Export failed. Please try again." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };

    const handleExportFilteredExcel = async () => {
        try {
            if (!destinationCountry) {
                setNotification({ value: true, text: "Please select Destination Country first." });
                setTimeout(() => setNotification({ value: false, text: "" }), 3000);
                return;
            }

            const allData = await fetchAllLeads();
            const filteredLeads = (allData || []).filter(
                (l) => String(l?.laneDestination?.country || "").toLowerCase() === destinationCountry.toLowerCase()
            );

            const leadRows = buildLeadExportRows(filteredLeads);
            const agentRows = buildAgentExportRows(agents);

            // ✅ Draft Table’a bas
            setDraftRows(leadRows);
            setDraftAgentRows(agentRows);

            exportWorkbook(
                [
                    { name: "Filtered Leads", rows: leadRows },
                    { name: "Matched Agents", rows: agentRows },
                ],
                `leads_${destinationCountry}_${new Date().toISOString().slice(0, 10)}.xlsx`
            );
        } catch (err) {
            console.error("Filtered export failed:", err);
            setNotification({ value: true, text: "Filtered export failed. Please try again." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };


    const MAIL_HEADER_HTML = `
  <div style="margin-bottom:10px;">
    <div style="font-size:14px;line-height:1.6;color:#111827;">
      Dear Partner,<br/>
     We are providing you with details of companies we can collaborate with.<br/>
    </div>
  </div>
`;

    const MAIL_FOOTER_HTML = `
  <div style="margin-top:10px;font-size:12px;color:#6b7280;">
    Best regards,<br/>
    Filenders RFQ Team
  </div>
`;

    const buildMailHtml = ({ leads, destination, excelLeadRows }) => `
  <div style="font-family:Arial, sans-serif; font-size:13px; color:#111;">
    ${MAIL_HEADER_HTML}

    <div style="font-size:16px;font-weight:800;margin-bottom:10px;">
      Sales Leads — Destination: ${escapeHtml(destination || "-")}
    </div>

    ${renderLeadCheckboxListHtml(leads)}

    ${renderExcelLeadsVerticalHtml(excelLeadRows || [])}

    ${MAIL_FOOTER_HTML}
  </div>
`;
    const plainTextVersion = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

    const sendEmailApi = async ({ to, cc, subject, body }) => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/email/Send-meeting-recap`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
                to: Array.isArray(to) ? to.join(",") : (to || ""),
                cc: cc || "",
                subject: subject || "",
                body: body || "",
                text: plainTextVersion(body),
            }),
        });

        const result = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(result?.error || "Email send failed");
        return result;
    };
    const clickAddHandler = () => {
        navigate("/sales-lead/create-sales-lead/details");
    };

    const dataTableData = useMemo(
        () => ({
            columns: [
                { Header: "Company", accessor: "customer" },
                { Header: "Country", accessor: "country" },
                { Header: "City", accessor: "city" },
                { Header: "Trade", accessor: "tradeType" },
                { Header: "Mode", accessor: "mode" },
                { Header: "Source Type", accessor: "sourceType" },
                { Header: "Status", accessor: "status" },
                { Header: "Actions", accessor: "actions", disableSortBy: true },
            ],
            rows: tableData,
        }),
        [tableData]
    );



    const getSelectedAgents = () => agents.filter((a) => selectedAgentIds.includes(a.id));

    const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

    const pickEmailsFromRepArray = (arr) =>
        uniq((Array.isArray(arr) ? arr : []).map((x) => x?.email).filter(Boolean));

    const buildAgentToEmails = (agentList) => {
        const emails = [];

        (agentList || []).forEach((a) => {
            if (a?.email) emails.push(a.email);
            if (!a?.email) {
                emails.push(...pickEmailsFromRepArray(a?.representatives));
                emails.push(...pickEmailsFromRepArray(a?.pricingRepresentatives));
                emails.push(...pickEmailsFromRepArray(a?.operationRepresentatives));
                emails.push(...pickEmailsFromRepArray(a?.financeRepresentatives));
            }
        });

        return uniq(
            emails
                .map((e) => String(e).trim().toLowerCase())
                .filter((e) => e.includes("@"))
        );
    };

    const escapeHtml = (s) =>
        String(s ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const renderLeadCheckboxListHtml = (leads) => {
        const items = (leads || []).map((l) => {
            const customer = escapeHtml(l?.customer || "-");
            const dest = escapeHtml(l?.laneDestination?.country || "-");
            const mode = escapeHtml(l?.serviceMode || "-");
            const incoterm = escapeHtml(l?.incoterm || "-");
            return `
      <div style="display:flex;align-items:center;gap:10px;padding:6px 0;">
        <span style="display:inline-block;width:14px;height:14px;border:1px solid #666;border-radius:2px;"></span>
        <span style="font-size:13px;"><b>${customer}</b> — Dest: ${dest} — Mode: ${mode} — Incoterm: ${incoterm}</span>
      </div>
    `;
        });

        return `
    <div style="border:1px solid #e5e5e5;border-radius:10px;padding:12px;margin:10px 0;">
      <div style="font-weight:700;margin-bottom:6px;">Filtered Sales Leads (${items.length})</div>
      ${items.join("") || "<div>-</div>"}
    </div>
  `;
    };


    const renderExcelLeadsVerticalHtml = (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        if (!list.length) return `<div style="margin-top:10px;">No lead rows.</div>`;

        const cols = Object.keys(list[0] || {});

        const renderRow = (label, value) => `
  <tr>
    <td style="
      padding:8px 10px;
      border:1px solid #e5e7eb;
      background:#f9fafb;

      width:140px;
      max-width:140px;

      font-weight:700;
      vertical-align:top;

      white-space:normal;
      word-break:break-word;
      line-height:1.35;
    ">
      ${escapeHtml(label)}
    </td>

    <td style="
      padding:8px 10px;
      border:1px solid #e5e7eb;
      vertical-align:top;

      /* ✅ uzun metinler satır satır */
      white-space:normal;
      word-break:break-word;
      overflow-wrap:anywhere;

      line-height:1.45;
      max-width:520px; /* ✅ mailde genişliği kontrol eder */
    ">
      ${escapeHtml(value ?? "")}
    </td>
  </tr>
`;

        const cards = list.map((r, idx) => {
            const title = r?.customer ? `Customer: ${r.customer}` : `Lead #${idx + 1}`;
            const rowsHtml = cols.map((c) => renderRow(getLeadLabel(c), r?.[c])).join("");
            return `
      <div style="border:1px solid #e5e7eb;border-radius:12px;padding:14px;margin:12px 0;background:#ffffff;">
        <div style="font-weight:800;font-size:14px;margin-bottom:10px;color:#111827;">
          ${escapeHtml(title)}
        </div>
            <table cellpadding="0" cellspacing="0" style="
              border-collapse:collapse;
              width:100%;
              font-size:12px;
              table-layout:fixed; 
            ">
                  ${rowsHtml}
        </table>
      </div>
    `;
        });

        return `
    <div style="margin-top:16px;">
      <div style="font-weight:800;margin:10px 0 8px 0;font-size:14px;color:#111827;">
        Lead Details (${list.length})
      </div>
      ${cards.join("")}
    </div>
  `;
    };

    const openDraftPreview = async () => {
        try {
            if (!destinationCountry) {
                setNotification({ value: true, text: "Please select Destination Country first." });
                setTimeout(() => setNotification({ value: false, text: "" }), 3000);
                return;
            }

            const selectedAgents = getSelectedAgents();
            if (!selectedAgents.length) {
                setNotification({ value: true, text: "Please select at least 1 agent." });
                setTimeout(() => setNotification({ value: false, text: "" }), 3000);
                return;
            }

            const all = await fetchAllLeads();
            const filteredLeads = (all || []).filter(
                (l) => String(l?.laneDestination?.country || "").toLowerCase() === destinationCountry.toLowerCase()
            );

            // ✅ excel export satırları yoksa burada da üretelim (garanti)
            const excelLeadRows = draftRows?.length ? draftRows : buildLeadExportRows(filteredLeads);

            // ✅ to list
            const to = buildAgentToEmails(selectedAgents);
            const cc = "emre.ayranci@filenders.com, rfq@filenders.com";
            const subject = `Sales Leads - Destination ${destinationCountry} (${filteredLeads.length})`;

            const htmlBody = buildMailHtml({
                leads: filteredLeads,
                destination: destinationCountry,
                excelLeadRows, // ✅ IMPORTANT
            });

            // ✅ Çoklu draft kuyruğu istiyorsan: her agent’a ayrı mail
            // Basit versiyon: tek mail (tüm agentlar to’da)
            const drafts = [
                { to: to.join(","), cc, subject, body: htmlBody }
            ];

            setPendingDrafts(drafts);
            setEmailPreviewData(drafts[0]);
            setEmailPreviewOpen(true);
        } catch (e) {
            console.error(e);
            setNotification({ value: true, text: "Draft could not be generated." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };
    const confirmAndSendEmail = async () => {
        try {
            const current = emailPreviewData;
            await sendEmailApi(current);

            // ✅ sıradaki draft’a geç
            const [, ...rest] = pendingDrafts;
            setPendingDrafts(rest);

            if (rest.length > 0) {
                setEmailPreviewData(rest[0]);
                setEmailPreviewOpen(true);
            } else {
                setEmailPreviewOpen(false);
            }

            setNotification({ value: true, text: `✅ Email sent to ${current.to}` });
            setTimeout(() => setNotification({ value: false, text: "" }), 3500);
        } catch (e) {
            console.error(e);
            setNotification({ value: true, text: `❌ Email send failed: ${e.message}` });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };

    useEffect(() => {
        if (!emailPreviewOpen) return;
        // ✅ sadece ilk açılışta DOM'a yaz
        requestAnimationFrame(() => {
            if (!bodyRef.current) return;
            const currentHtml = bodyRef.current.innerHTML || "";
            const nextHtml = emailPreviewData.body || "";
            if (currentHtml.trim() !== nextHtml.trim()) {
                bodyRef.current.innerHTML = nextHtml;
            }
        });
    }, [emailPreviewOpen]); // ✅ dependency sadece open
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Sales Leads"
                    subtitle="Sales leads by destination country, with matching global agents and email drafts."
                    actions={
                        <>
                            <CrmActionButton variant="secondary" icon={<FileDownloadOutlined />} onClick={handleExportAllLeadsExcel}>
                                Export All
                            </CrmActionButton>
                            <CrmActionButton
                                variant="secondary"
                                icon={<FilterAltOutlined />}
                                onClick={handleExportFilteredExcel}
                                disabled={!destinationCountry}
                            >
                                Export Filtered
                            </CrmActionButton>
                            <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                                Create Sales Lead
                            </CrmActionButton>
                        </>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

                <CrmFilterBar>
                    <MDBox sx={{ width: 280 }}>
                        <Autocomplete
                            size="small"
                            options={destinationOptions}
                            value={destinationCountry}
                            onChange={(_, val) => setDestinationCountry(val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Destination Country" placeholder="Select..."/>
                            )}
                            fullWidth
                            clearOnEscape
                        />
                    </MDBox>
                </CrmFilterBar>

                <CrmTableCard title="Sales Lead List" summary={`${tableData.length} leads`}>
                    <DataTable table={dataTableData} canSearch/>
                </CrmTableCard>

                {destinationCountry && (
                    <CrmCard>
                        <MDBox p={2.5}>
                            <CrmSectionHeader
                                title={`Global Agents · ${destinationCountry}`}
                                subtitle={agentsLoading ? "Loading..." : `${agents.length} agents · ${selectedAgentIds.length} selected`}
                                action={
                                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <CrmActionButton
                                            variant="secondary"
                                            onClick={() => setSelectedAgentIds(agents.map((a) => a.id))}
                                            disabled={!agents.length}
                                        >
                                            Select All
                                        </CrmActionButton>
                                        <CrmActionButton
                                            variant="secondary"
                                            onClick={() => setSelectedAgentIds([])}
                                            disabled={!selectedAgentIds.length}
                                        >
                                            Clear
                                        </CrmActionButton>
                                        <CrmActionButton
                                            icon={<MailOutline />}
                                            onClick={openDraftPreview}
                                            disabled={!destinationCountry || !selectedAgentIds.length}
                                        >
                                            Draft Preview
                                        </CrmActionButton>
                                    </MDBox>
                                }
                            />
                        </MDBox>
                        <DataTable table={agentDataTableData} canSearch isSorted={false} disableInternalSort/>
                    </CrmCard>
                )}
            </CrmPage>
                <Dialog open={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} maxWidth="lg" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700 }}>Email Draft Preview</DialogTitle>

                    <DialogContent dividers sx={{ pt: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {/* ✅ Fields dikey */}
                            <TextField
                                label="To"
                                value={emailPreviewData.to || ""}
                                onChange={(e) => setEmailPreviewData((p) => ({ ...p, to: e.target.value }))}
                                size="small"
                                fullWidth
                            />

                            <TextField
                                label="CC"
                                value={emailPreviewData.cc || ""}
                                onChange={(e) => setEmailPreviewData((p) => ({ ...p, cc: e.target.value }))}
                                size="small"
                                fullWidth
                            />

                            <TextField
                                label="Subject"
                                value={emailPreviewData.subject || ""}
                                onChange={(e) => setEmailPreviewData((p) => ({ ...p, subject: e.target.value }))}
                                size="small"
                                fullWidth
                            />

                            {/* ✅ Body: Preview + HTML Editor */}
                            <Box>
                                <MDTypography variant="button" fontWeight="medium">
                                    Body
                                </MDTypography>

                                {/* Preview (read-only) */}
                                <Box
                                    sx={{
                                        mt: 1,
                                        border: "1px solid #d0d0d0",
                                        borderRadius: 2,
                                        backgroundColor: "#f9f9f9",
                                        p: 2,
                                        minHeight: 320,
                                        maxHeight: "60vh",
                                        overflow: "auto",
                                    }}
                                >
                                    <div
                                        ref={bodyRef}
                                        contentEditable
                                        suppressContentEditableWarning
                                        onInput={() => {
                                            const html = bodyRef.current?.innerHTML ?? "";
                                            setEmailPreviewData((p) => ({ ...p, body: html }));
                                        }}
                                        style={{
                                            fontFamily: "'Segoe UI', sans-serif",
                                            fontSize: 14,
                                            lineHeight: 1.6,
                                            color: "#333",
                                            outline: "none",
                                            minWidth: 900,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <CrmActionButton variant="secondary" onClick={() => setEmailPreviewOpen(false)}>
                            Cancel
                        </CrmActionButton>
                        <CrmActionButton icon={<Send />} onClick={confirmAndSendEmail}>
                            Approve and Send
                        </CrmActionButton>
                    </DialogActions>
                </Dialog>
            <Footer />
        </DashboardLayout>
    );
}

export default SalesLeadTable;
